import { controller, target, targets } from '@github/catalyst'

type SelectedDate = {
    date: Date
    dateStr: string
}

@controller
export class DatePickerElement extends HTMLElement {
  @target manualForm: HTMLFormElement
  @target prevMonthForm: HTMLFormElement
  @target prevButton: HTMLButtonElement
  @target nextMonthForm: HTMLFormElement
  @target nextButton: HTMLButtonElement
  @target monthSelect: HTMLSelectElement
  @target yearSelect: HTMLSelectElement
  @target formInputTemplate: HTMLTemplateElement
  @target formInputs: HTMLDivElement
  @targets days: HTMLDivElement[]

  #abortController: AbortController
  #selectedDates: Map<string, SelectedDate> = new Map()

  connectedCallback() {
    const {signal} = (this.#abortController = new AbortController())
    this.addEventListener('change', this, {signal})
    this.addEventListener('submit', this, {signal})

    const preselectedDates = JSON.parse(this.getAttribute('data-preselected-dates') || '[]')

    for (const preselectedDate of preselectedDates) {
      this.#selectedDates.set(
        preselectedDate, {
          date: this.#parseDate(preselectedDate),
          dateStr: preselectedDate
        }
      )
    }

    if (this.querySelector(".CalendarMonth")) {
      this.#update()
    } else {
      const observer = new MutationObserver(() => {
        if (this.querySelector(".CalendarMonth")) {
          observer.disconnect()
          this.#update()
        }
      })

      observer.observe(this, {childList: true, subtree: true})
    }
  }

  disconnectedCallback() {
    this.#abortController.abort()
  }

  dayClick(event: Event) {
    const dayElem = event.target as HTMLElement
    const dateStr = dayElem.getAttribute('data-date')!

    if (dayElem.getAttribute('aria-selected')) {
      this.#selectedDates.delete(dateStr)
    } else {
      const date = this.#parseDate(dateStr)
      this.#selectedDates.set(dateStr, { date, dateStr })
    }

    this.#update()
  }

  handleEvent(event: Event) {
    if (event.target === this.monthSelect || event.target === this.yearSelect) {
      this.#handleManualDateChangeEvent(event)
    } else if (event.target === this.prevMonthForm || event.target === this.nextMonthForm) {
        this.#handleFormEvent(event)
    }
  }

  monthChangeSubmitStart() {
    this.style.opacity = '0.4'
    this.prevButton.disabled = true
    this.nextButton.disabled = true
  }

  monthChangeSubmitEnd() {
    this.style.opacity = '1'
    this.prevButton.disabled = false
    this.nextButton.disabled = false
  }

  #handleManualDateChangeEvent(event: Event) {
    if (event.type !== 'change') return

    this.manualForm.requestSubmit()
  }

  #handleFormEvent(event: Event) {
    this.style.opacity = '0.4'
  }

  #update() {
    const newFormInputs: HTMLInputElement[] = []

    for (const dayElem of this.days) {
      const dateStr = dayElem.getAttribute('data-date')!

      if (this.#selectedDates.has(dateStr)) {
        dayElem.setAttribute('aria-selected', 'true')
        const formInput = this.formInputTemplate.content.cloneNode(true) as HTMLInputElement;
        formInput.querySelector('input')!.setAttribute('value', dateStr)
        newFormInputs.push(formInput)
      } else {
        dayElem.removeAttribute('aria-selected')
      }
    }

    this.formInputs.replaceChildren(...newFormInputs)
  }

  #parseDate(dateStr: string): Date {
    return new Date(`${dateStr} 00:00:00`)
  }
}

declare global {
  interface Window {
    DatePickerElement: typeof DatePickerElement
  }

  interface HTMLElementTagNameMap {
    'date-picker': DatePickerElement
  }
}

if (!window.customElements.get('form-dialog')) {
  window.DatePickerElement = DatePickerElement
  window.customElements.define('form-dialog', DatePickerElement)
}
